import React, { useState, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { Form, Input, Select } from '@rocketseat/unform';
import { FaFileContract } from 'react-icons/fa';
import { toast } from 'react-toastify';
import parseISO from 'date-fns/parseISO';
import { Container, ButtonContainer, SearchSelect } from './styles';
import api from '~/services/api';
import FeesPreviewTable from '~/components/FeesPreviewTable';
import history from '~/services/history';

const formatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
export default function SubscriptionEditor({ location }) {
  const [uber, setUber] = useState(undefined);
  const [sales, setSales] = useState('');
  const [notes, setNotes] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const subscriptionItem =
    location.state && location.state.subscription
      ? location.state.subscription
      : undefined;

  const [subscription] = useState(subscriptionItem);

  useEffect(() => {
    async function loadClients() {
      const response = await api.get('/client');
      setClientOptions(
        response.data.map(clientItem => {
          return {
            id: clientItem.id,
            title: `${clientItem.fullName} (${clientItem.birthday})`,
          };
        })
      );
    }
    loadClients();
  }, []);

  useEffect(() => {
    async function loadVehicles() {
      const response = await api.get('/vehicle?subscribed=false');
      setVehicles(response.data);
      setVehicleOptions(
        response.data.map(vehicleItem => {
          return {
            id: vehicleItem.id,
            title: `${vehicleItem.displayName} (${vehicleItem.vin})`,
          };
        })
      );
    }
    loadVehicles();
  }, []);

  async function handleSubmit(data) {
    try {
      if (subscriptionItem) {
        const response = await api.put(
          `subscription/${subscriptionItem.id}`,
          data
        );
        history.push('/subscription');
        toast.success(
          `A subscription for '${response.data.client.fullName}' was successfully updated. (ID: ${response.data.id})`
        );
      } else {
        await api.post('subscription', data);
        toast.success(`Subscription was successfully created.`);
        history.push('/subscription');
      }
    } catch (err) {
      toast.error(
        err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Error while creating subscription'
      );
    }
  }

  const [startDateType, setStartDateType] = useState('text');
  const [vehicle, setVehicle] = useState(subscription && subscription.vehicle);
  const [start, setStart] = useState(
    subscription ? parseISO(subscription.start, 'MM/dd/yyyy') : new Date()
  );
  const [term, setTerm] = useState(subscription && subscription.term);

  const [activationValue, setActivationValue] = useState(
    subscription && subscription.activation
  );
  const [monthlyValue, setMonthlyValue] = useState(
    subscription && subscription.monthly
  );

  // useEffect(() => {
  //   async function loadSubscription() {
  //     try {
  //       const response = await api.get(`/subscription/${subscription}`);
  //       setSubscription(response.data);
  //       setVehicle(subscription.vehicle);
  //       setStart(parse(subscription.start, 'MM/dd/yyyy', new Date()));
  //       setTerm(subscription.term);
  //       setActivationValue(subscription.activation);
  //       setActivationValue(subscription.monthly);
  //     } catch (err) {
  //       toast.error(
  //         err.response && err.response.data && err.response.data.error
  //           ? err.response.data.error
  //           : 'Error while fetching subscription'
  //       );
  //     }
  //   }
  //   if (subscriptionId) {
  //     loadSubscription();
  //   }
  // }, [start, subscription]);

  const schema = Yup.object().shape({
    client_id: Yup.number('Should be a number')
      .nullable(true)
      .oneOf(
        [null, ...clientOptions.map(clientItem => clientItem.id)],
        'Should be a valid client'
      ),
    vehicle_id: Yup.number('Should be a number')
      .nullable(true)
      .oneOf(
        [null, ...vehicleOptions.map(vehicleItem => vehicleItem.id)],
        'Should be a valid vehicle'
      ),
    uber: Yup.string('Should be a string').transform((value, originalValue) => {
      return originalValue.trim() === '' ? 'false' : value;
    }),
    notes: Yup.string('Should be a string'),
    sales: Yup.string('Should be a string').required(),
    start: Yup.lazy(value =>
      value === ''
        ? Yup.string().strip(true)
        : Yup.date('Should be a valid date')
    ),
    term: Yup.number('Should be a number')
      .transform((value, originalValue) => {
        return originalValue.trim() === '' ? undefined : value;
      })
      .required(),
    monthly: Yup.number('Should be a number')
      .nullable(true)
      .transform((value, originalValue) =>
        originalValue.trim() === '' ? null : value
      ),
    activation: Yup.number('Should be a number')
      .nullable(true)
      .transform((value, originalValue) =>
        originalValue.trim() === '' ? null : value
      ),
  });

  const monthly = useMemo(() => {
    if (!vehicle) {
      return 'Select a vehicle first';
    }
    switch (term) {
      case '6': {
        if (uber === 'true') {
          return vehicle.monthlyUber.six;
        }
        return vehicle.monthly.six;
      }
      case '12':
        return vehicle.monthly.twelve;
      case '24':
        return vehicle.monthly.twentyFour;
      default:
        return 'Select a term first';
    }
  }, [term, uber, vehicle]);
  const activation = useMemo(() => {
    if (!vehicle) {
      return 'Select a vehicle first';
    }
    switch (term) {
      case '6': {
        if (uber === 'true') {
          return vehicle.activationUber.six;
        }
        return vehicle.activation.six;
      }
      case '12':
        return vehicle.activation.twelve;
      case '24':
        return vehicle.activation.twentyFour;
      default:
        return 'Select a term first';
    }
  }, [term, uber, vehicle]);

  return (
    <Container>
      <h1>Subscription</h1>
      <Form
        schema={schema}
        initialData={{ subscription: subscription && subscription.id }}
        onSubmit={handleSubmit}
      >
        <Input hidden name="subscription" />
        <SearchSelect
          name="client_id"
          label="Client"
          options={clientOptions}
          isSearchable
          placeholder={subscription && subscription.client.fullName}
        />
        <SearchSelect
          name="vehicle_id"
          label="Vehicle"
          options={vehicleOptions}
          isSearchable
          placeholder={subscription && subscription.vehicle.displayName}
          onChange={value => {
            const selectedVehicle = vehicles.find(
              vehicleItem => vehicleItem.id === value.id
            );
            setVehicle(selectedVehicle);
            if (!selectedVehicle.uber) {
              setUber(undefined);
            }
          }}
        />
        {vehicle && (
          <Select
            onChange={event => {
              setMonthlyValue('');
              setActivationValue('');
              if (event.target.value === 'true') {
                setTerm('6');
              }
              setUber(event.target.value);
            }}
            placeholder={
              !vehicle.uber ? 'NOT ALLOWED TO DO UBER' : 'Select an option'
            }
            name="uber"
            label="Will the client use for uber?"
            disabled={!vehicle.uber}
            options={[
              {
                id: true,
                title: 'Yes',
              },
              {
                id: false,
                title: 'No',
              },
            ]}
          />
        )}
        {vehicle && ((vehicle.uber && uber) || !vehicle.uber) && (
          <Select
            onChange={event => {
              if (event.target.value !== term) {
                setMonthlyValue('');
                setActivationValue('');
              }
              setTerm(event.target.value);
            }}
            placeholder={term}
            name="term"
            label="Select a Term:"
            options={
              uber === 'true'
                ? [
                    {
                      id: 6,
                      title: '6 Months',
                    },
                  ]
                : [
                    {
                      id: 6,
                      title: '6 Months',
                    },
                    {
                      id: 12,
                      title: '12 Months',
                    },
                    {
                      id: 24,
                      title: '24 Months',
                    },
                  ]
            }
          />
        )}
        {vehicle && term && ((vehicle.uber && uber) || !vehicle.uber) && (
          <>
            <Input
              label="Start Date"
              placeholder={
                typeof start === 'object' && start.toISOString().substr(0, 10)
              }
              type={startDateType}
              onFocus={() => {
                setStartDateType('date');
              }}
              onBlur={() => {
                setStartDateType('text');
              }}
              name="start"
              onChange={event => {
                const date =
                  event.target.value.trim() === ''
                    ? new Date()
                    : parseISO(event.target.value);
                setStart(date);
              }}
            />
            {/* <Input
              label="End Date"
              placeholder={
                typeof end === 'object' && end.toISOString().substr(0, 10)
              }
              type={endDateType}
              onFocus={() => {
                setEndDateType('date');
              }}
              onBlur={() => {
                setEndDateType('text');
              }}
              name="end"
              onChange={event => {
                const date =
                  event.target.value.trim() === ''
                    ? new Date()
                    : parseISO(event.target.value);
                setEnd(date);
              }}
            /> */}
            <Input
              label="Monthly"
              name="monthly"
              disabled={!term}
              placeholder={formatter.format(monthly)}
              value={monthlyValue}
              onChange={event => setMonthlyValue(event.target.value)}
            />
            <Input
              label="Activation"
              name="activation"
              disabled={!term}
              placeholder={formatter.format(activation)}
              value={activationValue}
              onChange={event => setActivationValue(event.target.value)}
            />
            <Input
              label="Sales"
              name="sales"
              value={sales}
              onChange={event => setSales(event.target.value)}
            />
            <Input
              label="Notes"
              name="notes"
              multiline
              value={notes}
              onChange={event => setNotes(event.target.value)}
            />
            <FeesPreviewTable
              start={start}
              term={term}
              vehicle={vehicle}
              uber={uber === 'true'}
              monthly={Number(monthlyValue) || Number(monthly)}
              activation={Number(activationValue) || Number(activation)}
            />
          </>
        )}
        <ButtonContainer>
          <button type="submit">
            <FaFileContract color="#FFF" size={22} />
            Create Subscription
          </button>
        </ButtonContainer>
      </Form>
    </Container>
  );
}

SubscriptionEditor.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      subscription: PropTypes.shape(),
    }),
  }).isRequired,
};
